import { pick } from "@/lib/languages";
import { companionL } from "@/i18n/search";

// Shared URL boundary. Legacy searches retain their adults-only interpretation.
// The owner's rule: a child under this age does not fly alone. A search with no
// adult and such a child needs the customer's word (accompanied=1) that an adult
// travels too; the passenger form asks again for each child under 12 by birth
// date, and the server refuses the booking without it (search_contract.py).
export const UNACCOMPANIED_MIN_AGE = 12;
// The server's bounds (search_contract.validate_ages / valid_party).
export const MAX_CHILDREN = 8;
export const MAX_TRAVELLERS = 9;
export const needsCompanion = (adults, childAges) =>
  adults === 0 && childAges.some((age) => age !== "" && Number.isFinite(Number(age)) && Number(age) < UNACCOMPANIED_MIN_AGE);
const integer = value => /^\d+$/.test(String(value)) && Number.isSafeInteger(Number(value));
const validDate = value => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value) || value.startsWith("0000")) return false;
  const date = new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value;
};

export function readFlightSearch(params) {
  const composition = params.has("adults") || params.has("child_ages");
  const adultsText = composition ? params.get("adults") : params.get("passengers") || "1";
  const adults = composition ? Number(adultsText) : parseInt(adultsText, 10) || 1;
  const childAges = params.has("child_ages") ? params.get("child_ages").split(",") : [];
  let error = null;
  if (composition && (!integer(adultsText) || adults < 0 || (adults === 0 && !childAges.length) ||
    childAges.some(age => !/^\d{1,2}$/.test(age) || Number(age) < 2 || Number(age) > 17) ||
    (params.has("passengers") && (!integer(params.get("passengers")) || Number(params.get("passengers")) !== adults + childAges.length)))) {
    error = "party";
  }
  const trip = params.get("trip");
  const legs = params.has("legs") ? params.get("legs").split(",").map(value => {
    const [from, to, date, ...extra] = value.split("|");
    return { from: from || "", to: to || "", date: date || "", extra };
  }) : [];
  if ((params.has("legs") && trip !== "multi") || (trip === "multi" && (
    params.has("return_date") || legs.length < 2 || legs.length > 5 || legs.some((leg, index) =>
      leg.extra.length || !/^[A-Za-z]{3}$/.test(leg.from) || !/^[A-Za-z]{3}$/.test(leg.to) ||
      leg.from.toUpperCase() === leg.to.toUpperCase() || !validDate(leg.date) || (index > 0 && leg.date < legs[index - 1].date))
  ))) error = "legs";
  if (trip && !["round", "oneway", "multi"].includes(trip)) error = "legs";
  if (childAges.length || trip === "multi") {
    if (adults < 0 || adults + childAges.length < 1 || adults + childAges.length > MAX_TRAVELLERS || childAges.length > MAX_CHILDREN) error = "party";
    if (!/^[A-Za-z]{3}$/.test(params.get("origin_city") || "") ||
      !/^[A-Za-z]{3}$/.test(params.get("destination_city") || "") || !validDate(params.get("date") || "") ||
      (params.get("return_date") && (!validDate(params.get("return_date")) || params.get("return_date") < params.get("date")))) error = "legs";
    if (trip === "multi" && legs.length && (
      (params.get("origin_city") || "").toUpperCase() !== legs[0].from.toUpperCase() ||
      (params.get("destination_city") || "").toUpperCase() !== legs[0].to.toUpperCase() ||
      params.get("date") !== legs[0].date)) error = "legs";
  }
  if (!error && needsCompanion(adults, childAges) && params.get("accompanied") !== "1") error = "companion";
  // Duplicated advanced fields are ambiguous and must not be silently collapsed.
  if (["adults", "child_ages", "legs", "trip", "passengers"].some(key => params.getAll(key).length > 1)) error = "party";
  return { adults, childAges, legs, error, apiParams: {
    adults,
    ...(childAges.length ? { child_ages: childAges.join(",") } : {}),
    ...(trip === "multi" ? { trip: "multi", legs: params.get("legs") } : {}),
  } };
}

export function flightSearchError(error, lang) {
  if (!error) return "";
  if (error === "companion") return pick(companionL, lang).searchRequired;
  if (error === "party") return lang === "fr"
    ? "Indiquez de 1 à 9 voyageurs et l’âge de chaque enfant (2 à 17 ans)."
    : "Enter 1 to 9 travelers and each child’s age (2–17).";
  return lang === "fr"
    ? "Indiquez 2 à 5 vols avec des codes aéroport distincts à 3 lettres et des dates valides dans l’ordre chronologique."
    : "Enter 2–5 flights with distinct three-letter airport codes and valid dates in chronological order.";
}
